'use client';

import type { ProfileOption } from '@components/Profile/UtilsComponents/ProfilesSelect';
import { cardEditContext } from '@context/card-edit-context';
import type { ProfileEssentialDetailsFragment } from '@graphql/fragments/profile/core.graphql';
import {
  type Dispatch,
  type FC,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';

import type { CardFlavour } from '../types';
import type { CardAttributeType, CardField } from '.cache/__types__';

interface CardEditContextProps {
  cardId: string | undefined;
  activeAttributeId: string | undefined;
  editPageMode?: 'create' | 'update';
  flavour: CardFlavour | undefined;
  requestToMakeActive: (attributeId: string, editPageMode: 'create' | 'update') => void;
  makeInactive: (attributeId: string) => void;
  onEditEvent?: (event: 'open' | 'save' | 'cancel') => void;
}

interface CardEditContextProviderProps {
  flavour: CardFlavour;
  canAssignMultipleProfiles?: boolean;
  selectedProfiles?: readonly ProfileOption[];
  onSelectNewProfiles?: (profiles: readonly ProfileOption[]) => void;
  selectedProfilesContainInvalidEmails?: boolean;
  selectedProfilesExceedMaximum?: boolean;
  selectedProfilesWithInvalidDomains?: string[];
  isCardOwner?: boolean | undefined;
  loading?: boolean | undefined;
  cardProfile?: ProfileEssentialDetailsFragment;
  children: ReactNode;
  showRequiredAttributesErrors?: boolean | undefined;
  setShowRequiredAttributesErrors?: Dispatch<SetStateAction<boolean>>;
  showSsoDomainError?: boolean | undefined;
  setShowSsoDomainError?: Dispatch<SetStateAction<boolean>>;
  onEditEvent?: (event: 'open' | 'save' | 'cancel' | 'delete', attributeName: string) => void;
}

export const CardEditContextProvider: FC<CardEditContextProviderProps> = ({
  flavour,
  canAssignMultipleProfiles,
  selectedProfiles,
  onSelectNewProfiles,
  selectedProfilesContainInvalidEmails,
  selectedProfilesExceedMaximum,
  selectedProfilesWithInvalidDomains,
  isCardOwner,
  loading,
  cardProfile,
  children,
  showRequiredAttributesErrors,
  setShowRequiredAttributesErrors,
  showSsoDomainError,
  setShowSsoDomainError,
  onEditEvent,
}) => {
  const [activeAttribute, setActiveAttribute] = useState<CardAttributeType | undefined>();
  const [pendingCardField, setPendingCardField] = useState<CardField | undefined>();
  const [activeAttributeId, setActiveAttributeId] = useState<string>();
  const [editPageMode, setEditPageMode] = useState<CardEditContextProps['editPageMode']>();
  const [cardId, setCardId] = useState<string | undefined>(undefined);

  const activeAttributeIdRef = useRef('');

  const handleRequestToMakeActive = useCallback((attributeId: string, editPageMode: 'create' | 'update') => {
    ReactDOM.unstable_batchedUpdates(() => {
      setActiveAttributeId(attributeId);
      setEditPageMode(editPageMode);
    });

    activeAttributeIdRef.current = attributeId;
  }, []);

  const handleMakeInactive = useCallback((attributeId?: string) => {
    // We use a ref here instead of state because we want to be sure that even if this is triggered in the same
    // event bubbling sequence that we are looking at the actual active element id, and not a piece of old state
    // due to closures.
    if (activeAttributeIdRef.current === attributeId) {
      ReactDOM.unstable_batchedUpdates(() => {
        setActiveAttributeId(undefined);
        setEditPageMode(undefined);
      });
    }
  }, []);

  const handleSetPendingCardField = (field: CardField | undefined) => {
    setPendingCardField(field);
  };

  const handleSetActiveAttribute = (attribute: CardAttributeType | undefined) => {
    setActiveAttribute(attribute);
  };

  const handleSetCardId = useCallback((id: string | undefined) => {
    setCardId(id);
  }, []);

  return (
    <cardEditContext.Provider
      value={{
        cardId,
        setCardId: handleSetCardId,
        canAssignMultipleProfiles,
        selectedProfiles,
        onSelectNewProfiles,
        selectedProfilesContainInvalidEmails,
        selectedProfilesExceedMaximum,
        selectedProfilesWithInvalidDomains,
        makeInactive: handleMakeInactive,
        requestToMakeActive: handleRequestToMakeActive,
        editPageMode,
        flavour,
        activeAttributeId,
        setPendingCardField: handleSetPendingCardField,
        pendingCardField,
        activeAttribute,
        setActiveAttribute: handleSetActiveAttribute,
        loading,
        cardProfile,
        isCardOwner,
        showRequiredAttributesErrors,
        setShowRequiredAttributesErrors,
        showSsoDomainError,
        setShowSsoDomainError,
        onEditEvent,
      }}
    >
      {children}
    </cardEditContext.Provider>
  );
};
